import React, { createContext, useState, useContext, ReactNode } from 'react';
import { UserLocation } from '../../redux/user/userSlice';

interface LocationSelectorContextType {
  isChangeLocationModalOpen: boolean;
  setIsChangeLocationModalOpen: (isOpen: boolean) => void;
  isAddNewLocationModalOpen: boolean;
  setIsAddNewLocationModalOpen: (isOpen: boolean) => void;
  isRemoveLocationModalOpen: boolean;
  setIsRemoveLocationModalOpen: (isOpen: boolean) => void;
  newGeneralLocationData: NewGeneralLocationDataProps;
  setNewGeneralLocationData: (data: NewGeneralLocationDataProps) => void;
  setNewGeneralLocationCountriesOptions: (options: SelectOptionProps[]) => void;
  setNewGeneralLocationCitiesOptions: (options: SelectOptionProps[]) => void;
  setNewGeneralLocationModalClose: () => void;
  onLocationSelect: (location: UserLocation) => void;
  selectedLocation?: UserLocation;
  showError?: boolean;
}

const LocationSelectorContext = createContext<
  LocationSelectorContextType | undefined
>(undefined);

interface SelectOptionProps {
  value: string;
  label: string;
}

export interface NewGeneralLocationDataProps {
  country?: SelectOptionProps;
  city?: SelectOptionProps;
  options: {
    countries: SelectOptionProps[];
    cities: SelectOptionProps[];
  };
  openedModal: 'country' | 'city' | undefined;
}

const initialNewGeneralLocationData = {
  country: { value: 'LT', label: 'Lietuva (Lithuania)' },
  city: { value: '', label: '' },
  options: {
    countries: [],
    cities: [],
  },
  openedModal: undefined,
};

export const LocationSelectorProvider: React.FC<{
  children: ReactNode;
  onLocationSelect: (location: UserLocation) => void;
  selectedLocation?: UserLocation;
  showError?: boolean;
}> = ({ children, onLocationSelect, selectedLocation, showError }) => {
  const [isChangeLocationModalOpen, setIsChangeLocationModalOpen] =
    useState(false);
  const [isAddNewLocationModalOpen, setIsAddNewLocationModalOpen] =
    useState(false);
  const [isRemoveLocationModalOpen, setIsRemoveLocationModalOpen] =
    useState(false);
  const [newGeneralLocationData, setNewGeneralLocationData] =
    useState<NewGeneralLocationDataProps>(initialNewGeneralLocationData);

  const setNewGeneralLocationCountriesOptions = (
    options: SelectOptionProps[],
  ) => {
    setNewGeneralLocationData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        countries: options,
      },
    }));
  };

  const setNewGeneralLocationCitiesOptions = (options: SelectOptionProps[]) => {
    setNewGeneralLocationData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        cities: options,
      },
    }));
  };

  const setNewGeneralLocationModalClose = () => {
    setNewGeneralLocationData((prev) => ({
      ...prev,
      openedModal: undefined,
    }));
  };

  return (
    <LocationSelectorContext.Provider
      value={{
        onLocationSelect,
        selectedLocation,
        showError,
        isChangeLocationModalOpen,
        setIsChangeLocationModalOpen,
        isAddNewLocationModalOpen,
        setIsAddNewLocationModalOpen,
        isRemoveLocationModalOpen,
        setIsRemoveLocationModalOpen,
        newGeneralLocationData,
        setNewGeneralLocationData,
        setNewGeneralLocationCountriesOptions,
        setNewGeneralLocationCitiesOptions,
        setNewGeneralLocationModalClose,
      }}
    >
      {children}
    </LocationSelectorContext.Provider>
  );
};

export const useLocationSelectorContext = () => {
  const context = useContext(LocationSelectorContext);
  if (context === undefined) {
    throw new Error(
      'useLocationSelector must be used within a LocationSelectorProvider',
    );
  }
  return context;
};
